import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Share2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ShareProgressDialogProps {
  challengeName?: string;
  challengeId?: string;
}

export function ShareProgressDialog({ challengeName, challengeId }: ShareProgressDialogProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [shareType, setShareType] = useState("custom");
  const [message, setMessage] = useState("");
  const [visibility, setVisibility] = useState("partners");

  const shareMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const shareData: any = { message };
      
      if (challengeName) {
        shareData.challenge_name = challengeName;
        shareData.challenge_id = challengeId;
      }

      const { error } = await supabase
        .from('shared_progress')
        .insert({
          user_id: user.id,
          share_type: shareType,
          share_data: shareData,
          visibility,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['progress-feed'] });
      toast.success("Progress shared!");
      setOpen(false);
      setMessage("");
      setShareType("custom");
      setVisibility("partners");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to share progress");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Share2 className="h-4 w-4" />
          Share Progress
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share Your Progress</DialogTitle>
          <DialogDescription>
            Let your accountability partners know about your wins!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">What are you sharing?</Label>
            <Select value={shareType} onValueChange={setShareType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">General Update</SelectItem>
                <SelectItem value="challenge_completed">Challenge Completed</SelectItem>
                <SelectItem value="goal_achieved">Goal Achieved</SelectItem>
                <SelectItem value="milestone">Milestone Reached</SelectItem>
                <SelectItem value="streak">Streak Achievement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {challengeName && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">Challenge: {challengeName}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Share your thoughts, feelings, or what helped you succeed..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="visibility">Who can see this?</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partners">My Partners Only</SelectItem>
                <SelectItem value="public">Everyone</SelectItem>
                <SelectItem value="private">Just Me</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => shareMutation.mutate()}
              disabled={!message.trim() || shareMutation.isPending}
              className="flex-1"
            >
              Share
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
